import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, 
  Search, 
  Receipt,
  Loader2,
  FileText,
  Calendar,
  Building2,
  Users,
  DollarSign,
  ChevronDown,
  ChevronRight,
  Eye,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Pencil,
  Save,
  Download,
  Printer,
  Mail
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ClientTicketsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedTicket, setExpandedTicket] = useState(null);
  
  // Create ticket dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [allJobs, setAllJobs] = useState([]);
  const [jobsWithTimesheets, setJobsWithTimesheets] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);  // Stores job_id_productType composite key
  const [selectedJobNumber, setSelectedJobNumber] = useState(null);
  const [selectedProductType, setSelectedProductType] = useState(null);  // "cuttings" or "shavings"
  const [selectedDate, setSelectedDate] = useState('');
  const [eligibility, setEligibility] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [creating, setCreating] = useState(false);
  const [manualOverride, setManualOverride] = useState(false);
  const [manualLineItems, setManualLineItems] = useState([{ description: '', qty: 0, uom: 'loads', rate: 0, unit_name: '' }]);
  const [manualClientName, setManualClientName] = useState('');
  const [manualSiteName, setManualSiteName] = useState('');

  // View ticket dialog
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingTicket, setViewingTicket] = useState(null);

  // Edit ticket dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [editLineItems, setEditLineItems] = useState([]);
  const [editCrew, setEditCrew] = useState([]);
  const [editNotes, setEditNotes] = useState('');
  const [editJobDescription, setEditJobDescription] = useState('');
  const [editSpecialInstructions, setEditSpecialInstructions] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editJobNumber, setEditJobNumber] = useState('');
  const [editBolNumber, setEditBolNumber] = useState('');
  const [editClientName, setEditClientName] = useState('');
  const [editContactName, setEditContactName] = useState('');
  const [editContactPhone, setEditContactPhone] = useState('');
  const [editSiteName, setEditSiteName] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  // Handle search term from Invoice Review navigation
  useEffect(() => {
    if (location.state?.searchTerm) {
      setSearchTerm(location.state.searchTerm);
      // Clear the navigation state to prevent persisting on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.searchTerm]);

  // Handle prefill from dispatch board navigation
  useEffect(() => {
    if (location.state?.openCreate) {
      // Open the create dialog with prefilled data
      const prefill = location.state.prefill || {};
      
      // Fetch jobs first, then open dialog with prefilled values
      const initWithPrefill = async () => {
        await fetchAllJobs();
        await fetchJobsWithTimesheets();
        
        // Set prefilled values
        if (prefill.job_number) {
          setSelectedJob(prefill.job_number);
        }
        if (prefill.date) {
          setSelectedDate(prefill.date);
        }
        
        setShowCreateDialog(true);
        
        // Clear the navigation state to prevent reopening on refresh
        navigate(location.pathname, { replace: true, state: {} });
      };
      
      initWithPrefill();
    }
  }, [location.state]);

  // Auto-check eligibility when job and date are both selected
  useEffect(() => {
    if (selectedJob && selectedDate) {
      checkEligibility();
    }
  }, [selectedJob, selectedDate]);

  const fetchTickets = async () => {
    const token = localStorage.getItem('vaultai_token');
    try {
      const response = await axios.get(`${API}/client-tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setTickets(response.data.tickets || []);
      }
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchJobsWithTimesheets = async () => {
    const token = localStorage.getItem('vaultai_token');
    try {
      // Fetch all jobs with expand_types=true for Client Tickets
      // This shows separate entries for jobs with multiple types (e.g., #689 CUTTINGS, #689 SHAVINGS)
      const jobsResponse = await axios.get(`${API}/dispatch/jobs?expand_types=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (jobsResponse.data.success) {
        setAllJobs(jobsResponse.data.jobs || []);
      }
      
      // Also fetch jobs with timesheets for reference
      const tsResponse = await axios.get(`${API}/client-tickets-with-timesheets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (tsResponse.data.success) {
        setJobsWithTimesheets(tsResponse.data.jobs || []);
      }
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    }
  };

  const checkEligibility = async () => {
    if (!selectedJob || !selectedDate) return;
    
    const token = localStorage.getItem('vaultai_token');
    setCheckingEligibility(true);
    setEligibility(null);
    
    try {
      const response = await axios.post(`${API}/client-tickets/check-eligibility`, {
        job_number: selectedJobNumber,
        job_id: selectedJob,
        product_type: selectedProductType,
        date: selectedDate
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEligibility(response.data);
    } catch (error) {
      console.error('Failed to check eligibility:', error);
      toast.error('Failed to check eligibility');
    } finally {
      setCheckingEligibility(false);
    }
  };

  const createTicket = async () => {
    if (!selectedJob || !selectedDate) return;
    
    const token = localStorage.getItem('vaultai_token');
    setCreating(true);
    
    try {
      // Extract the base job_id from the composite key (job_id_product)
      const parts = selectedJob.split('_');
      // The last part is the product type (cuttings/shavings/unknown), remove it to get job_id
      const baseJobId = parts.length > 1 ? parts.slice(0, -1).join('_') : selectedJob;
      
      const payload = {
        job_number: selectedJobNumber,
        job_id: baseJobId,
        product_type: selectedProductType,
        date: selectedDate,
      };
      
      if (manualOverride) {
        payload.manual_override = true;
        payload.manual_line_items = manualLineItems.filter(li => li.description.trim());
        if (manualClientName.trim()) payload.manual_client_name = manualClientName;
        if (manualSiteName.trim()) payload.manual_site_name = manualSiteName;
      }
      
      const response = await axios.post(`${API}/client-tickets`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Check for BOL date warning
        if (response.data.warning) {
          toast.warning(response.data.warning, { duration: 10000 });
        }
        
        // Check if multiple tickets were created
        if (response.data.ticket_count && response.data.ticket_count > 1) {
          const ticketNumbers = response.data.tickets.map(t => `#${t.ticket_number}`).join(' and ');
          toast.success(`Created ${response.data.ticket_count} separate tickets: ${ticketNumbers}`);
        } else {
          const ticket = response.data.ticket || response.data.tickets?.[0];
          const hasTimesheet = ticket?.has_timesheet;
          toast.success(
            `Draft Ticket #${ticket.ticket_number} (${selectedProductType?.toUpperCase() || ''}) created!${!hasTimesheet ? ' (No timesheet - add before approving)' : ''}`
          );
        }
        setShowCreateDialog(false);
        resetCreateDialog();
        fetchTickets();
      } else {
        toast.error(response.data.error || 'Failed to create ticket');
      }
    } catch (error) {
      console.error('Failed to create ticket:', error);
      toast.error('Failed to create ticket');
    } finally {
      setCreating(false);
    }
  };

  const deleteTicket = async (ticket) => {
    if (!window.confirm(`Delete ticket #${ticket.ticket_number}?`)) return;
    
    const token = localStorage.getItem('vaultai_token');
    try {
      await axios.delete(`${API}/client-tickets/${ticket.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ticket deleted');
      fetchTickets();
    } catch (error) {
      console.error('Failed to delete ticket:', error);
      toast.error('Failed to delete ticket');
    }
  };

  const exportTicket = async (ticket) => {
    if (ticket.status !== 'approved') {
      toast.error('Only approved tickets can be exported');
      return;
    }
    
    const token = localStorage.getItem('vaultai_token');
    try {
      toast.info('Generating Excel file...');
      const response = await axios.get(`${API}/client-tickets/${ticket.id}/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CT_${ticket.ticket_number}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel file downloaded!');
    } catch (error) {
      console.error('Failed to export ticket:', error);
      toast.error(error.response?.data?.detail || 'Failed to export ticket');
    }
  };

  const exportTicketPDF = async (ticket) => {
    if (ticket.status !== 'approved') {
      toast.error('Only approved tickets can be exported');
      return;
    }
    
    const token = localStorage.getItem('vaultai_token');
    try {
      toast.info('Generating PDF with scale receipts...');
      const response = await axios.get(`${API}/client-tickets/${ticket.id}/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CT_${ticket.ticket_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('Failed to export ticket to PDF:', error);
      toast.error(error.response?.data?.detail || 'Failed to export PDF');
    }
  };

  // Save email as Gmail draft with client ticket PDF attached
  const sendTicketEmail = async (ticket) => {
    const token = localStorage.getItem('vaultai_token');
    
    try {
      toast.info('Creating email draft...');
      
      const response = await axios.post(
        `${API}/client-tickets/${ticket.id}/send-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message || `Draft saved — check Gmail to review and send`);
      } else {
        toast.error(response.data.error || 'Failed to create draft');
      }
    } catch (error) {
      console.error('Failed to create email draft:', error);
      toast.error(error.response?.data?.detail || 'Failed to create email draft');
    }
  };

  const openCreateDialog = () => {
    fetchJobsWithTimesheets();
    setShowCreateDialog(true);
  };

  const resetCreateDialog = () => {
    setSelectedJob(null);
    setSelectedJobNumber(null);
    setSelectedProductType(null);
    setSelectedDate('');
    setEligibility(null);
    setManualOverride(false);
    setManualLineItems([{ description: '', qty: 0, uom: 'loads', rate: 0, unit_name: '' }]);
    setManualClientName('');
    setManualSiteName('');
  };

  const viewTicket = (ticket) => {
    setViewingTicket(ticket);
    setShowViewDialog(true);
  };

  const editTicket = (ticket) => {
    setEditingTicket(ticket);
    setEditLineItems(ticket.line_items || []);
    setEditCrew((ticket.crew || []).map(m => ({
      name: m.name || '',
      unit_number: m.unit_number || m.unit_name || '',
      position: m.position || 'Driver'
    })));
    setEditNotes(ticket.notes || '');
    setEditJobDescription(ticket.job_description || '');
    setEditSpecialInstructions(ticket.special_instructions || '');
    setEditStatus(ticket.status || 'draft');
    setEditDate(ticket.date || '');
    setEditJobNumber(ticket.job_number || '');
    setEditBolNumber(ticket.bol_number || '');
    setEditClientName(ticket.client_name || '');
    setEditContactName(ticket.contact_name || '');
    setEditContactPhone(ticket.contact_phone || '');
    setEditSiteName(ticket.site_name || '');
    setEditLocation(ticket.location || '');
    setShowEditDialog(true);
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...editLineItems];
    updated[index] = { ...updated[index], [field]: value };
    // Recalculate total
    if (field === 'qty' || field === 'rate') {
      const qty = parseFloat(field === 'qty' ? value : updated[index].qty) || 0;
      const rate = parseFloat(field === 'rate' ? value : updated[index].rate) || 0;
      updated[index].total = Math.round(qty * rate * 100) / 100;
    }
    setEditLineItems(updated);
  };

  const addLineItem = () => {
    setEditLineItems([
      ...editLineItems,
      {
        description: '',
        uom: 'hrs',
        qty: 0,
        rate: 0,
        total: 0,
        driver_name: ''
      }
    ]);
  };

  const removeLineItem = (index) => {
    if (editLineItems.length <= 1) {
      toast.error('Must have at least one line item');
      return;
    }
    const updated = editLineItems.filter((_, i) => i !== index);
    setEditLineItems(updated);
  };

  const saveTicket = async () => {
    if (!editingTicket) return;
    
    const token = localStorage.getItem('vaultai_token');
    setSaving(true);
    
    try {
      const response = await axios.put(`${API}/client-tickets/${editingTicket.id}`, {
        status: editStatus,
        date: editDate,
        job_number: editJobNumber,
        bol_number: editBolNumber,
        client_name: editClientName,
        contact_name: editContactName,
        contact_phone: editContactPhone,
        site_name: editSiteName,
        location: editLocation,
        line_items: editLineItems,
        crew: editCrew,
        notes: editNotes,
        job_description: editJobDescription,
        special_instructions: editSpecialInstructions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Ticket updated!');
        setShowEditDialog(false);
        fetchTickets();
      } else {
        toast.error(response.data.error || 'Failed to update ticket');
      }
    } catch (error) {
      console.error('Failed to save ticket:', error);
      const msg = error?.response?.data?.detail || error?.response?.data?.error || 'Failed to save ticket';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save ticket');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Handle YYYY-MM-DD format to avoid timezone issues
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-');
      return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return <Badge variant="secondary">Draft</Badge>;
      case 'pending':
        return <Badge variant="outline" className="border-yellow-500 text-yellow-500">Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="border-green-500 text-green-500">Approved</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    if (statusFilter !== 'all' && ticket.status !== statusFilter) return false;
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    return (
      ticket.ticket_number?.toString().includes(search) ||
      ticket.job_number?.toLowerCase().includes(search) ||
      ticket.client_name?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="client-tickets-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Receipt className="h-6 w-6 text-blue-600" />
            Client Tickets
          </h1>
          <p className="text-slate-500 text-sm">Bills of Lading and client invoicing</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="create-ticket-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Ticket
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by ticket #, job #, or client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="tickets-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tickets List */}
      <div className="grid gap-3">
        {filteredTickets.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-slate-500">
              {searchTerm || statusFilter !== 'all' 
                ? 'No tickets match your filters' 
                : 'No tickets yet. Click "Create Ticket" to create one.'}
            </CardContent>
          </Card>
        ) : (
          filteredTickets.map((ticket) => (
            <Card 
              key={ticket.id}
              className={`transition-all ${expandedTicket === ticket.id ? 'ring-2 ring-blue-500' : ''}`}
              data-testid={`ticket-card-${ticket.ticket_number}`}
            >
              <CardContent className="p-4">
                {/* Ticket Header */}
                <div className="flex items-center justify-between">
                  <div 
                    className="flex items-center gap-3 flex-1 cursor-pointer"
                    onClick={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)}
                  >
                    {expandedTicket === ticket.id ? (
                      <ChevronDown className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    )}
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-blue-600">#{ticket.ticket_number}</span>
                        <span className="text-slate-400">|</span>
                        <span className="font-medium">Job #{ticket.job_number}</span>
                        {ticket.product_type && (
                          <Badge variant="secondary" className="text-xs">
                            {ticket.product_type.toUpperCase()}
                          </Badge>
                        )}
                        {ticket.unit_name && (
                          <Badge variant="outline" className="text-xs border-blue-400 text-blue-500">
                            Unit {ticket.unit_name}
                          </Badge>
                        )}
                        {getStatusBadge(ticket.status)}
                        {!ticket.has_timesheet && (
                          <Badge variant="outline" className="border-amber-500 text-amber-500 text-xs">
                            No Timesheet
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          {ticket.client_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(ticket.date)}
                        </span>
                        <span className="flex items-center gap-1 font-medium text-green-600">
                          <DollarSign className="h-3 w-3" />
                          {formatCurrency(ticket.grand_total || ticket.subtotal || ticket.line_items?.reduce((sum, item) => sum + (item.total || 0), 0) || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {ticket.status === 'approved' && (
                      <>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => exportTicket(ticket)} 
                          title="Export to Excel"
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          data-testid={`export-ticket-${ticket.ticket_number}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => exportTicketPDF(ticket)} 
                          title="Export to PDF (with Scale Receipts)"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`export-pdf-${ticket.ticket_number}`}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => sendTicketEmail(ticket)} 
                          title="Save Email Draft in Gmail"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          data-testid={`email-ticket-${ticket.ticket_number}`}
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => editTicket(ticket)} title="Edit">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => viewTicket(ticket)} title="View">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => deleteTicket(ticket)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedTicket === ticket.id && (
                  <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 space-y-4">
                    {/* Line Items */}
                    {ticket.line_items && ticket.line_items.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">LINE ITEMS</span>
                        <div className="mt-2 space-y-1">
                          {ticket.line_items.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm bg-slate-100 dark:bg-slate-800 rounded px-3 py-2">
                              <span className="text-slate-900 dark:text-slate-100">{item.description || item.name}</span>
                              <div className="flex items-center gap-4">
                                <span className="text-slate-600 dark:text-slate-300">{item.qty} {item.uom}</span>
                                <span className="text-slate-600 dark:text-slate-300">@ {formatCurrency(item.rate)}</span>
                                <span className="font-medium text-slate-900 dark:text-slate-100">{formatCurrency(item.total)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Crew */}
                    {ticket.crew && ticket.crew.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400 flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          CREW
                        </span>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {ticket.crew.map((member, idx) => (
                            <Badge key={idx} variant="outline">
                              {member.name} {member.unit_number && `(Unit ${member.unit_number})`}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Site Info */}
                    {ticket.site_name && (
                      <div>
                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">SITE</span>
                        <p className="text-sm font-medium text-slate-800 dark:text-white">{ticket.site_name} {ticket.location && <span className="text-slate-600 dark:text-slate-300">- {ticket.location}</span>}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Ticket Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Draft Ticket</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <AlertCircle className="h-4 w-4 inline mr-2" />
              You can create a draft ticket now. A timesheet entry is required before finalizing/approving.
            </div>

            {/* Job Selection - Shows job+product combinations (multiple units allowed) */}
            <div>
              <label className="text-sm font-medium mb-1 block">Select Job & Product Type</label>
              <Select value={selectedJob || ''} onValueChange={(v) => { 
                // Find job from the selected value
                const jobFromTs = jobsWithTimesheets.find(j => {
                  const uniqueKey = `${j.job_id}_${j.product_type || 'unknown'}`;
                  return uniqueKey === v;
                });
                const jobFromAll = allJobs.find(j => j.id === v);
                const job = jobFromTs || jobFromAll;
                
                setSelectedJob(v); 
                setSelectedJobNumber(job?.job_number || '');
                setSelectedProductType(job?.product_type || job?.job_types?.[0]?.toLowerCase() || null);
                setSelectedDate(''); 
                setEligibility(null); 
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a job..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {/* Show jobs with timesheets (product-type split) — these are the main selection options.
                      Sort numerically by job_number (then by product_type) and dedupe by
                      (job_number, product_type) so the list is stable and predictable. */}
                  {(() => {
                    const seen = new Set();
                    const deduped = [];
                    for (const j of jobsWithTimesheets) {
                      const key = `${j.job_number || ''}::${(j.product_type || 'unknown').toLowerCase()}`;
                      if (seen.has(key)) continue;
                      seen.add(key);
                      deduped.push(j);
                    }
                    // Numeric sort on job_number, fallback to string; secondary by product_type
                    deduped.sort((a, b) => {
                      const na = parseInt(String(a.job_number || '').replace(/\D/g, ''), 10);
                      const nb = parseInt(String(b.job_number || '').replace(/\D/g, ''), 10);
                      const aNum = Number.isFinite(na) ? na : Number.POSITIVE_INFINITY;
                      const bNum = Number.isFinite(nb) ? nb : Number.POSITIVE_INFINITY;
                      if (aNum !== bNum) return aNum - bNum;
                      const s = String(a.job_number || '').localeCompare(String(b.job_number || ''));
                      if (s !== 0) return s;
                      return String(a.product_type || '').localeCompare(String(b.product_type || ''));
                    });
                    return deduped.map((job) => {
                      const uniqueKey = `${job.job_id}_${job.product_type || 'unknown'}`;
                      const jobType = job.job_type_label || job.job_types?.[0]?.toUpperCase() || '';
                      const unitsDisplay = job.unit_names?.length > 0 ? `Unit ${job.unit_names.join(', Unit ')}` : '';
                      return (
                        <SelectItem key={uniqueKey} value={uniqueKey} className="py-2" data-testid={`job-option-${job.job_number}`}>
                          <span className="font-medium">#{job.job_number}</span>
                          {jobType && <span className="text-muted-foreground ml-1">({jobType})</span>}
                          {unitsDisplay && <span className="text-blue-500 text-xs ml-1">[{unitsDisplay}]</span>}
                          <span className="text-muted-foreground ml-1">- {job.client_name}</span>
                          <span className="text-green-500 ml-1">✓</span>
                        </SelectItem>
                      );
                    });
                  })()}
                </SelectContent>
              </Select>
            </div>

            {/* Date Selection */}
            {selectedJob && (
              <div>
                <label className="text-sm font-medium mb-1 block">Select Date</label>
                <Input 
                  type="date" 
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
                {(() => {
                  // Handle product-type-split keys (job_id_product)
                  const jobTs = jobsWithTimesheets.find(j => {
                    const uniqueKey = `${j.job_id}_${j.product_type || 'unknown'}`;
                    return uniqueKey === selectedJob || j.job_id === selectedJob;
                  });
                  if (jobTs?.dates?.length > 0) {
                    return (
                      <p className="text-xs text-slate-500 mt-1">
                        Dates with timesheets: {jobTs.dates.map(d => formatDate(d)).join(', ')}
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>
            )}

            {/* Preview info when job and date selected */}
            {selectedJob && selectedDate && (
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 text-sm">
                <p className="font-medium text-slate-900 dark:text-white">Draft Ticket Preview</p>
                <p className="text-slate-600 dark:text-slate-300">
                  Job: #{selectedJobNumber} 
                  {selectedProductType && <span className="ml-1">({selectedProductType.toUpperCase()})</span>}
                </p>
                <p className="text-slate-600 dark:text-slate-300">Date: {formatDate(selectedDate)}</p>
                <p className="text-slate-600 dark:text-slate-300">
                  Status: Draft {manualOverride 
                    ? '(Manual Override)' 
                    : eligibility?.timesheet_count > 0
                      ? '(Ready to finalize)' 
                      : '(Needs timesheet to finalize)'}
                </p>
              </div>
            )}

            {/* Manual Override (Admin only) */}
            {selectedJob && selectedDate && (
              <div className="border rounded-lg p-3 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="manual-override-toggle">
                  <input type="checkbox" checked={manualOverride} onChange={e => setManualOverride(e.target.checked)}
                    className="rounded border-input" />
                  <span className="text-sm font-medium">Manual Override</span>
                  <span className="text-xs text-muted-foreground">(skip timesheets/scale data, enter line items manually)</span>
                </label>

                {manualOverride && (
                  <div className="space-y-3 pt-2 border-t">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">Client Name (override)</label>
                        <Input className="h-8 text-xs" value={manualClientName} onChange={e => setManualClientName(e.target.value)} placeholder="Leave blank to use job's client" />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">Site Name (override)</label>
                        <Input className="h-8 text-xs" value={manualSiteName} onChange={e => setManualSiteName(e.target.value)} placeholder="Leave blank to use job's site" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Line Items</label>
                      <div className="space-y-1.5">
                        {manualLineItems.map((li, idx) => (
                          <div key={idx} className="flex gap-2 items-end">
                            <div className="w-20"><Input className="h-8 text-xs" placeholder="Unit #" value={li.unit_name || ''}
                              onChange={e => { const n = [...manualLineItems]; n[idx] = {...n[idx], unit_name: e.target.value}; setManualLineItems(n); }} /></div>
                            <div className="flex-1"><Input className="h-8 text-xs" placeholder="Description" value={li.description}
                              onChange={e => { const n = [...manualLineItems]; n[idx] = {...n[idx], description: e.target.value}; setManualLineItems(n); }} /></div>
                            <div className="w-20"><Input className="h-8 text-xs" type="number" placeholder="Qty" value={li.qty}
                              onChange={e => { const n = [...manualLineItems]; n[idx] = {...n[idx], qty: parseFloat(e.target.value)||0}; setManualLineItems(n); }} /></div>
                            <div className="w-24">
                              <select className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs" value={li.uom}
                                onChange={e => { const n = [...manualLineItems]; n[idx] = {...n[idx], uom: e.target.value}; setManualLineItems(n); }}>
                                <option value="loads">Loads</option><option value="tonnes">Tonnes</option><option value="yards">Yards</option>
                                <option value="hours">Hours</option><option value="each">Each</option>
                              </select>
                            </div>
                            <div className="w-24"><Input className="h-8 text-xs" type="number" step="0.01" placeholder="Rate $" value={li.rate}
                              onChange={e => { const n = [...manualLineItems]; n[idx] = {...n[idx], rate: parseFloat(e.target.value)||0}; setManualLineItems(n); }} /></div>
                            <div className="w-20 text-xs font-mono text-right pt-1">${(li.qty * li.rate).toFixed(2)}</div>
                            {manualLineItems.length > 1 && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => setManualLineItems(manualLineItems.filter((_,i) => i !== idx))}><Trash2 className="h-3.5 w-3.5" /></Button>
                            )}
                          </div>
                        ))}
                      </div>
                      <Button variant="outline" size="sm" className="mt-2 text-xs" onClick={() => setManualLineItems([...manualLineItems, { description: '', qty: 0, uom: 'loads', rate: 0, unit_name: '' }])}>
                        <Plus className="h-3 w-3 mr-1" />Add Line Item
                      </Button>
                      <div className="text-right text-sm font-bold mt-1">
                        Total: ${manualLineItems.reduce((s, li) => s + (li.qty * li.rate), 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetCreateDialog(); }}>
              Cancel
            </Button>
            <Button 
              onClick={createTicket} 
              disabled={!selectedJob || !selectedDate || creating}
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Ticket Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Ticket #{viewingTicket?.ticket_number}
            </DialogTitle>
          </DialogHeader>
          
          {viewingTicket && (
            <div className="space-y-6 py-4">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4 bg-slate-100 dark:bg-slate-800 rounded-lg p-4">
                <div>
                  <span className="text-xs text-slate-500 dark:text-slate-400">BOL Number</span>
                  <p className="font-medium text-slate-900 dark:text-slate-100">{viewingTicket.bol_number}</p>
                </div>
                <div>
                  <span className="text-xs text-slate-500 dark:text-slate-400">Job Number</span>
                  <p className="font-medium text-slate-900 dark:text-slate-100">
                    {viewingTicket.job_number}
                    {viewingTicket.product_type && (
                      <Badge variant="secondary" className="ml-2 text-xs">
                        {viewingTicket.product_type.toUpperCase()}
                      </Badge>
                    )}
                    {viewingTicket.unit_name && (
                      <Badge variant="outline" className="ml-2 text-xs border-blue-400 text-blue-500">
                        Unit {viewingTicket.unit_name}
                      </Badge>
                    )}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-slate-500 dark:text-slate-400">Date</span>
                  <p className="font-medium text-slate-900 dark:text-slate-100">{formatDate(viewingTicket.date)}</p>
                </div>
                <div>
                  <span className="text-xs text-slate-500 dark:text-slate-400">Status</span>
                  <p>{getStatusBadge(viewingTicket.status)}</p>
                </div>
              </div>

              {/* Client Info */}
              <div>
                <h3 className="font-medium mb-2 flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Customer
                </h3>
                <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4 space-y-1">
                  <p className="font-medium text-slate-900 dark:text-slate-100">{viewingTicket.client_name}</p>
                  {viewingTicket.contact_name && (
                    <p className="text-sm text-slate-700 dark:text-slate-300">Contact: {viewingTicket.contact_name} {viewingTicket.contact_phone && `(${viewingTicket.contact_phone})`}</p>
                  )}
                  {viewingTicket.site_name && (
                    <p className="text-sm font-medium text-slate-800 dark:text-white">Site: {viewingTicket.site_name}</p>
                  )}
                  {viewingTicket.location && (
                    <p className="text-sm text-slate-700 dark:text-slate-200">Location: {viewingTicket.location}</p>
                  )}
                </div>
              </div>

              {/* Line Items Table */}
              <div>
                <h3 className="font-medium mb-2">Items</h3>
                <table className="w-full border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                  <thead className="bg-slate-200 dark:bg-slate-700">
                    <tr>
                      <th className="text-left p-2 text-sm text-slate-900 dark:text-slate-100">Description</th>
                      <th className="text-center p-2 text-sm text-slate-900 dark:text-slate-100">Unit #</th>
                      <th className="text-right p-2 text-sm text-slate-900 dark:text-slate-100">UoM</th>
                      <th className="text-right p-2 text-sm text-slate-900 dark:text-slate-100">Qty</th>
                      <th className="text-right p-2 text-sm text-slate-900 dark:text-slate-100">Rate</th>
                      <th className="text-right p-2 text-sm text-slate-900 dark:text-slate-100">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-slate-900">
                    {viewingTicket.line_items?.map((item, idx) => (
                      <tr key={idx} className="border-t border-slate-200 dark:border-slate-700">
                        <td className="p-2 text-sm text-slate-900 dark:text-slate-100">{item.description || item.name}</td>
                        <td className="p-2 text-sm text-center text-slate-900 dark:text-slate-100">{item.unit_name || item.unit_number || '-'}</td>
                        <td className="p-2 text-sm text-right text-slate-900 dark:text-slate-100">{item.uom}</td>
                        <td className="p-2 text-sm text-right text-slate-900 dark:text-slate-100">{item.qty}</td>
                        <td className="p-2 text-sm text-right text-slate-900 dark:text-slate-100">{formatCurrency(item.rate)}</td>
                        <td className="p-2 text-sm text-right font-medium text-slate-900 dark:text-slate-100">{formatCurrency(item.total)}</td>
                      </tr>
                    ))}
                    <tr className="border-t border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800">
                      <td colSpan={5} className="p-2 text-sm font-medium text-right text-slate-900 dark:text-slate-100">Subtotal</td>
                      <td className="p-2 text-sm font-bold text-right text-green-600">
                        {formatCurrency(viewingTicket.grand_total || viewingTicket.subtotal || viewingTicket.line_items?.reduce((sum, item) => sum + (item.total || 0), 0) || 0)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Crew */}
              {viewingTicket.crew && viewingTicket.crew.length > 0 && (
                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Crew
                  </h3>
                  <table className="w-full border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                    <thead className="bg-slate-200 dark:bg-slate-700">
                      <tr>
                        <th className="text-left p-2 text-sm text-slate-900 dark:text-slate-100">Position</th>
                        <th className="text-left p-2 text-sm text-slate-900 dark:text-slate-100">Name</th>
                        <th className="text-left p-2 text-sm text-slate-900 dark:text-slate-100">Unit #</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-slate-900">
                      {viewingTicket.crew.map((member, idx) => (
                        <tr key={idx} className="border-t border-slate-200 dark:border-slate-700">
                          <td className="p-2 text-sm text-slate-900 dark:text-slate-100">{member.position}</td>
                          <td className="p-2 text-sm font-medium text-slate-900 dark:text-slate-100">{member.name}</td>
                          <td className="p-2 text-sm text-slate-900 dark:text-slate-100">{member.unit_number || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            {viewingTicket?.status === 'approved' && (
              <>
                <Button 
                  onClick={() => exportTicket(viewingTicket)}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="view-dialog-export-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export to Excel
                </Button>
                <Button 
                  onClick={() => exportTicketPDF(viewingTicket)}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="view-dialog-export-pdf-btn"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Export to PDF
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Ticket Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Edit Ticket #{editingTicket?.ticket_number}
            </DialogTitle>
          </DialogHeader>
          
          {editingTicket && (
            <div className="space-y-6 py-4">
              {/* Ticket Info Row */}
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Status</label>
                  <Select value={editStatus} onValueChange={setEditStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Date</label>
                  <Input 
                    type="date"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Job Number</label>
                  <Input 
                    value={editJobNumber}
                    onChange={(e) => setEditJobNumber(e.target.value)}
                    placeholder="Job #"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">BOL Number</label>
                  <Input 
                    value={editBolNumber}
                    onChange={(e) => setEditBolNumber(e.target.value)}
                    placeholder="BOL #"
                  />
                </div>
              </div>

              {!editingTicket.has_timesheet && editStatus !== 'draft' && (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  ⚠ Cannot approve without a timesheet entry for this job and date
                </p>
              )}

              {/* Customer Info */}
              <div className="border rounded-lg p-4 space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Customer Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Client Name</label>
                    <Input 
                      value={editClientName}
                      onChange={(e) => setEditClientName(e.target.value)}
                      placeholder="Client name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Contact Name</label>
                    <Input 
                      value={editContactName}
                      onChange={(e) => setEditContactName(e.target.value)}
                      placeholder="Contact name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Contact Phone</label>
                    <Input 
                      value={editContactPhone}
                      onChange={(e) => setEditContactPhone(e.target.value)}
                      placeholder="Phone number"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Site Name</label>
                    <Input 
                      value={editSiteName}
                      onChange={(e) => setEditSiteName(e.target.value)}
                      placeholder="Site name"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium mb-1 block">Location</label>
                    <Input 
                      value={editLocation}
                      onChange={(e) => setEditLocation(e.target.value)}
                      placeholder="Location / Address"
                    />
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Line Items</label>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={addLineItem}
                    className="h-8"
                    data-testid="add-line-item-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Line Item
                  </Button>
                </div>
                <table className="w-full border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                  <thead className="bg-slate-200 dark:bg-slate-700">
                    <tr>
                      <th className="text-left p-2 text-sm text-slate-900 dark:text-slate-100">Description</th>
                      <th className="text-left p-2 text-sm text-slate-900 dark:text-slate-100 w-32">Driver</th>
                      <th className="text-center p-2 text-sm text-slate-900 dark:text-slate-100 w-20">Unit #</th>
                      <th className="text-center p-2 text-sm text-slate-900 dark:text-slate-100 w-20">UoM</th>
                      <th className="text-center p-2 text-sm text-slate-900 dark:text-slate-100 w-20">Qty</th>
                      <th className="text-center p-2 text-sm text-slate-900 dark:text-slate-100 w-24">Rate</th>
                      <th className="text-right p-2 text-sm text-slate-900 dark:text-slate-100 w-24">Total</th>
                      <th className="text-center p-2 text-sm text-slate-900 dark:text-slate-100 w-12"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-slate-900">
                    {editLineItems.map((item, idx) => (
                      <tr key={idx} className="border-t border-slate-200 dark:border-slate-700">
                        <td className="p-2">
                          <Input 
                            value={item.description || item.name || ''} 
                            onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                            className="h-8 text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800"
                            placeholder="Enter description..."
                          />
                        </td>
                        <td className="p-2">
                          <Input 
                            value={item.driver_name || ''} 
                            onChange={(e) => updateLineItem(idx, 'driver_name', e.target.value)}
                            className="h-8 text-xs text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800"
                            placeholder="Driver"
                          />
                        </td>
                        <td className="p-2">
                          <Input 
                            value={item.unit_name || item.unit_number || ''} 
                            onChange={(e) => updateLineItem(idx, 'unit_name', e.target.value)}
                            className="h-8 text-center text-xs text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800"
                            placeholder="Unit #"
                          />
                        </td>
                        <td className="p-2">
                          <Input 
                            value={item.uom || ''} 
                            onChange={(e) => updateLineItem(idx, 'uom', e.target.value)}
                            className="h-8 text-center text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800"
                          />
                        </td>
                        <td className="p-2">
                          <Input 
                            type="number"
                            value={item.qty || 0} 
                            onChange={(e) => updateLineItem(idx, 'qty', parseFloat(e.target.value) || 0)}
                            className="h-8 text-center text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800"
                          />
                        </td>
                        <td className="p-2">
                          <Input 
                            type="number"
                            step="0.01"
                            value={item.rate || 0} 
                            onChange={(e) => updateLineItem(idx, 'rate', parseFloat(e.target.value) || 0)}
                            className="h-8 text-center text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800"
                          />
                        </td>
                        <td className="p-2 text-right font-medium text-slate-900 dark:text-slate-100">
                          {formatCurrency(item.total || 0)}
                        </td>
                        <td className="p-2 text-center">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLineItem(idx)}
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Remove line item"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800">
                      <td colSpan={6} className="p-2 text-sm font-medium text-right text-slate-900 dark:text-slate-100">Subtotal</td>
                      <td className="p-2 text-sm font-bold text-right text-green-600">
                        {formatCurrency(editLineItems.reduce((sum, item) => sum + (item.total || 0), 0))}
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Job Description */}
              <div>
                <label className="text-sm font-medium mb-2 block">Job Description</label>
                <Textarea 
                  value={editJobDescription}
                  onChange={(e) => setEditJobDescription(e.target.value)}
                  placeholder="Enter job description..."
                  rows={2}
                />
              </div>

              {/* Crew */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Crew (drivers + units on this ticket)</label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditCrew([...editCrew, { name: '', unit_number: '', position: 'Driver' }])}
                    data-testid="add-crew-btn"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add Crew
                  </Button>
                </div>
                <div className="space-y-2">
                  {editCrew.length === 0 ? (
                    <div className="text-xs text-muted-foreground italic px-2 py-3 border border-dashed rounded text-center">
                      No crew on this ticket. Click "Add Crew" if the AI missed someone.
                    </div>
                  ) : (
                    editCrew.map((member, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Input
                          value={member.name}
                          onChange={(e) => {
                            const copy = [...editCrew];
                            copy[idx] = { ...copy[idx], name: e.target.value };
                            setEditCrew(copy);
                          }}
                          placeholder="Driver name"
                          className="flex-1 h-9"
                          data-testid={`crew-name-${idx}`}
                        />
                        <Input
                          value={member.unit_number}
                          onChange={(e) => {
                            const copy = [...editCrew];
                            copy[idx] = { ...copy[idx], unit_number: e.target.value };
                            setEditCrew(copy);
                          }}
                          placeholder="Unit #"
                          className="w-28 h-9"
                          data-testid={`crew-unit-${idx}`}
                        />
                        <Input
                          value={member.position}
                          onChange={(e) => {
                            const copy = [...editCrew];
                            copy[idx] = { ...copy[idx], position: e.target.value };
                            setEditCrew(copy);
                          }}
                          placeholder="Position"
                          className="w-32 h-9"
                          data-testid={`crew-position-${idx}`}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditCrew(editCrew.filter((_, i) => i !== idx))}
                          className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Remove crew member"
                          data-testid={`crew-remove-${idx}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-sm font-medium mb-2 block">Notes</label>
                <Textarea 
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Internal notes..."
                  rows={2}
                />
              </div>

              {/* Special Instructions */}
              <div>
                <label className="text-sm font-medium mb-2 block">Special Instructions</label>
                <Textarea 
                  value={editSpecialInstructions}
                  onChange={(e) => setEditSpecialInstructions(e.target.value)}
                  placeholder="Any special instructions..."
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveTicket} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
